// Where compiled rules are actually written.
//
// The engine has to work in three places at once: a shopper's page, the builder's iframe, and a test with
// no DOM at all. So the registry never touches a document itself — it writes to a host, and this file is
// the only implementation that knows what a `<style>` element is. A test hands it a fake instead.
//
// One `<style data-xshop-elements>` per document, found before it is made, so two element trees mounted on
// one page share a single sheet rather than each growing one of their own. Inserts are queued and written
// once on the next microtask: a page of two hundred elements mounts in one React commit, and appending two
// hundred times would have the browser reparse the sheet two hundred times before it painted once.

/** Where compiled rules are written: a `<style>` element in a browser, a fake in a test, nothing on a server. */
export type StyleSheetHost = { insert(css: string): void; reset(css: string): void };

/** The host used where there is no document to write to, so a server render is a no-op rather than a throw. */
export const NO_STYLE_HOST: StyleSheetHost = { insert: () => {}, reset: () => {} };

const DOCUMENT_NODE = 9;
const SHEET_MARK = 'data-xshop-elements';

// Answers the document a root belongs to, which for a document is itself and for a shadow root is its owner.
function ownerDocument(root: Document | ShadowRoot): Document | null {
  return root.nodeType === DOCUMENT_NODE ? (root as Document) : root.ownerDocument;
}

// Finds the one `<style>` element this root keeps every element rule in, making it on first use.
export function styleElement(root: Document | ShadowRoot): HTMLStyleElement | null {
  const document = ownerDocument(root);
  if (document === null) return null;
  const container = root.nodeType === DOCUMENT_NODE ? document.head : (root as ShadowRoot);
  if (container === null) return null;
  const found = container.querySelector(`style[${SHEET_MARK}]`);
  if (found !== null) return found as HTMLStyleElement;
  const created = document.createElement('style');
  created.setAttribute(SHEET_MARK, '');
  container.appendChild(created);
  return created;
}

// Writes every rule queued since the last flush into the sheet in one append, so the browser reparses once.
function flushInto(element: HTMLStyleElement, queued: string[]): void {
  if (queued.length === 0) return;
  const text = queued.join('');
  queued.length = 0;
  element.appendChild(element.ownerDocument.createTextNode(text));
}

// Builds the host that writes into one document's or one shadow root's own element stylesheet.
export function documentStyleHost(root: Document | ShadowRoot): StyleSheetHost {
  const element = styleElement(root);
  if (element === null) return NO_STYLE_HOST;
  const queued: string[] = [];
  let scheduled = false;
  return {
    // Queues one rule and schedules the single append that writes everything queued, before the next paint.
    insert(css: string): void {
      queued.push(css);
      if (scheduled) return;
      scheduled = true;
      queueMicrotask(() => { scheduled = false; flushInto(element, queued); });
    },
    // Replaces the whole sheet, which is what a sweep of the rules nothing wears any more leaves behind.
    reset(css: string): void {
      queued.length = 0;
      element.textContent = css;
    }
  };
}
